import React, { FunctionComponent, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ScrollView, View, ViewStyle } from 'react-native'

import { BlurView } from 'expo-blur'
import { LinearGradient } from 'expo-linear-gradient'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { NAV_HEADER_HEIGHT } from '@lyrahealth-inc/shared-app-logic/src/features/header/constants'
import {
  FavoritedProvidersEntrypointNavProps,
  NavigationItem,
} from '@lyrahealth-inc/shared-app-logic/src/features/header/types'
import { getIsActiveNavItem } from '@lyrahealth-inc/shared-app-logic/src/features/header/utils'

import { AuroraIconCircle } from './utils'
import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { HeaderMessagingIcon } from '../../atoms/headerMessagingIcon/HeaderMessagingIcon'
import { ArrowIcon, ArrowIconDirection } from '../../atoms/icons/ArrowIcon'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { LyraLogo } from '../../atoms/icons/LyraLogo'
import { SearchStarIcon } from '../../atoms/icons/SearchStarIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { SecondaryIconButton } from '../../atoms/secondaryIconButton/SecondaryIconButton'
import { Title } from '../../atoms/title/Title'
import { XButton } from '../../atoms/xButton/XButton'
import { IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { FavoritedProvidersStarIconButton } from '../../molecules/favoritedProvidersStarIconButton/FavoritedProvidersStarIconButton'
import { FloatingTabBar, FloatingTabBarTab } from '../../molecules/floatingTabBar/FloatingTabBar'
import { LanguageSelector } from '../../molecules/languageSelector/LanguageSelector'
import { ReferButton } from '../../molecules/referButton/ReferButton'
import { TabBar } from '../../molecules/tabBar/TabBar'
import { Toolbar } from '../../molecules/toolbar/Toolbar'
import { ToolbarTab } from '../../molecules/toolbar/toolbarTab/ToolbarTab'
import { getFocusBoxShadow } from '../../styles/commonStyles'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { RowView } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { GradientHeader } from '../gradientHeader/GradientHeader'
import { HeaderNavMenu, HeaderNavMenuProps } from '../headerNavMenu/HeaderNavMenu'

export type LightHomeToolbarProps = {
  testID?: string
  /** Care programs */
  tabs?: Array<{ name: string; tabId: string }>
  activeTabId?: string
  onTabPressed?: (tabId: string) => void
  /** Main toolbar title */
  title?: string | JSX.Element
  /** Title text size */
  textSize?: TitleSize
  topInset?: number
  showBack?: boolean
  onBackPressed?: () => void
  showNavMenu?: boolean
  showMessages?: boolean
  unreadMessages?: number
  onMessagesPressed?: () => void
  messagesPopover?: JSX.Element
  shouldShowCareNavigatorHeaderEntrypoint?: boolean
  careNavigatorModal?: React.ReactElement
  careNavigatorEntryPoint?: React.ReactElement
  isInternational?: boolean
  showClose?: boolean
  onClosePressed?: () => void
  showExit?: boolean
  showLogo?: boolean
  onLogoPressed?: () => void
  headerNavMenuProps?: HeaderNavMenuProps
  navItems?: NavigationItem[]
  currentPathname?: string
  currentScreen?: string
  favoritedProvidersEntrypointNavProps?: FavoritedProvidersEntrypointNavProps
  shouldShowLoginAndSignUpButtons?: boolean
  onLoginPressed?: () => void
  onSignUpPressed?: () => void
  shouldShowReferral?: boolean
  onReferralPressed?: () => void
  /** Removes the white background and bottom border. Adjusts padding. */
  isTransparentBackground?: boolean
  /** Hides the bottom border when isTransparentBackground is false */
  hideBorder?: boolean
  /** Makes the toolbar overlap the page content by setting height to 0 */
  shouldOverlapPageContent?: boolean
  shouldShowLanguageSelector?: boolean
  onLanguageSelectorGetHelpPress?: () => void
  beforeLaunch?: boolean
  showAICoachIcon?: boolean
  shouldShowSearch?: boolean
  onSearchPressed?: () => void
  isBlurBackground?: boolean
  logoFillColor?: string
}

const Container = styled.View<{
  theme: ThemeType
  isTransparentBackground?: boolean
  hideBorder?: boolean
  hidePadding?: boolean
  isWebBlur?: boolean
}>(
  ({
    theme: { colors, spacing, breakpoints, isAuroraTheme },
    isTransparentBackground,
    hideBorder,
    hidePadding,
    isWebBlur,
  }) => {
    let padding = '0px'
    if (breakpoints.isMinWidthDesktop) {
      padding = spacing['48px']
    } else if (breakpoints.isMinWidthTablet) {
      padding = spacing['16px']
    }
    const hasSolidWebNavHeader =
      IS_WEB && isAuroraTheme && colors.backgroundNavHeader !== 'transparent' && !isWebBlur && !isTransparentBackground
    const solidBackground = hasSolidWebNavHeader
      ? {
          backgroundColor: colors.backgroundNavHeader,
          ...(!hideBorder &&
            !isTransparentBackground && { borderBottomWidth: '1px', borderBottomColor: colors.dividerSecondary }),
        }
      : {}
    const nonTransparentBackground =
      !hasSolidWebNavHeader && !isTransparentBackground
        ? {
            backgroundColor: colors.backgroundPrimary,
            ...(!hideBorder && { borderBottomWidth: '1px', borderBottomColor: colors.dividerSecondary }),
          }
        : {}
    const transparentBackground =
      !hasSolidWebNavHeader && isTransparentBackground
        ? { paddingHorizontal: padding, backgroundColor: 'transparent' }
        : {}
    return {
      flexDirection: 'column',
      ...solidBackground,
      ...nonTransparentBackground,
      ...transparentBackground,
      ...(isWebBlur && { backdropFilter: 'blur(30px)', backgroundColor: 'transparent' }),
      ...(hidePadding && { padding: '0px' }),
    }
  },
)

const WebScrollGradient = styled(LinearGradient)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
})

const HeaderRow = styled.View<{ theme: ThemeType; isBlur: boolean }>(({ theme, isBlur }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  minHeight: theme.breakpoints.isMobileSized ? '56px' : '62px',
  alignItems: 'center',
  ...(IS_WEB && isBlur
    ? { paddingHorizontal: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['32px'] }
    : {
        marginHorizontal: isBlur ? 0 : theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['32px'],
      }),
}))

const LeftContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  zIndex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.isAuroraTheme ? theme.spacing['16px'] : 0,
}))

const RightContainer = styled(RowView)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: 'auto',
  alignItems: 'center',
  gap: theme.isAuroraTheme ? theme.spacing['16px'] : 0,
}))

const MenuButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.isAuroraTheme ? 0 : theme.spacing['16px'],
  alignSelf: 'center',
}))

const CareNavigatorButtonContainer = styled.View<{ theme: ThemeType; isOnLeft?: boolean }>(({ theme, isOnLeft }) => ({
  marginLeft: isOnLeft ? 0 : theme.spacing['16px'],
}))

const StyledXButton = styled(XButton)({
  alignSelf: 'center',
  top: 0,
})

const LogoButtonContainer = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  padding: `${theme.spacing['4px']} ${theme.spacing['8px']}`,
  borderRadius: theme.spacing['8px'],
}))

const MessagesPopoverContainer = styled(View)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.isAuroraTheme ? 0 : theme.spacing['16px'],
  borderRadius: theme.spacing['8px'],
}))

const PageTitleContainer = styled.View({
  position: 'absolute',
  left: 0,
  right: 0,
})

const NavItemsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['48px'],
  flexDirection: 'row',
  gap: theme.spacing['24px'],
}))

const NavItemButton = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['8px'],
  borderRadius: theme.spacing['8px'],
}))

const FavoritesButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.isAuroraTheme ? 0 : theme.spacing['16px'],
  marginRight: theme.isAuroraTheme ? 0 : theme.spacing['16px'],
  alignSelf: 'center',
}))

const NavItemButtonActiveUnderline = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '100%',
  height: '3px',
  backgroundColor: theme.colors.backgroundActive,
  position: 'absolute',
  borderTopLeftRadius: theme.spacing['16px'],
  borderTopRightRadius: theme.spacing['16px'],
  left: 0,
  right: 0,
  bottom: theme.isAuroraTheme ? '-9px' : '-12px',
  marginLeft: 'auto',
  marginRight: 'auto',
}))

const LoginAndSignUpContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.isAuroraTheme ? 0 : theme.spacing['24px'],
  flexDirection: 'row',
  gap: theme.isAuroraTheme ? theme.spacing['16px'] : 0,
}))

const LoginButton = styled(SecondaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.isAuroraTheme ? 0 : theme.spacing['16px'],
}))

const SecondarySignUpButton = styled(SecondaryButton)({
  marginLeft: 0,
})

const SignUpButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : theme.spacing['32px'],
}))

const SecondaryIconButtonStyled = styled(SecondaryIconButton)(({ theme }) => ({
  marginLeft: theme.isAuroraTheme ? 0 : theme.spacing['16px'],
  ...(theme.isAuroraTheme && { borderWidth: 0.5 }),
}))

const BlurViewStyled = styled(BlurView)<{
  theme: ThemeType
  minNavBarHeight: number
  negativeTopInset: number
  isTransparentBackground?: boolean
}>(({ theme, minNavBarHeight, negativeTopInset, isTransparentBackground }) => {
  let padding = '0px'
  if (isTransparentBackground) {
    if (theme.breakpoints.isMinWidthDesktop) {
      padding = '80px'
    } else if (theme.breakpoints.isMinWidthTablet) {
      padding = theme.spacing['48px']
    } else {
      padding = theme.spacing['24px']
    }
  } else {
    if (theme.breakpoints.isMinWidthDesktop) {
      padding = theme.spacing['48px']
    } else if (theme.breakpoints.isMinWidthTablet) {
      padding = theme.spacing['16px']
    } else {
      padding = theme.spacing['8px']
    }
  }

  return {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    height: IS_WEB ? '100%' : minNavBarHeight,
    alignItems: 'center',
    paddingHorizontal: padding,
    paddingTop: -negativeTopInset,
    top: 0,
    width: '100%',
  }
})

/**
 * Tool bar component
 * - If in the mobile app - use `AppHeader`
 */
export const LightHomeToolbar: FunctionComponent<LightHomeToolbarProps> = ({
  testID,
  tabs = [],
  activeTabId = '',
  onTabPressed,
  title,
  topInset = 0,
  showBack = false,
  onBackPressed,
  showNavMenu = true,
  showMessages = false,
  unreadMessages = 0,
  onMessagesPressed,
  messagesPopover,
  textSize = TitleSize.SMALL,
  shouldShowCareNavigatorHeaderEntrypoint = false,
  careNavigatorEntryPoint,
  careNavigatorModal,
  showClose = false,
  showLogo = true,
  onLogoPressed,
  showExit = false,
  onClosePressed,
  headerNavMenuProps,
  navItems,
  currentPathname,
  currentScreen,
  favoritedProvidersEntrypointNavProps,
  shouldShowLoginAndSignUpButtons = false,
  onLoginPressed,
  onSignUpPressed,
  shouldShowReferral,
  onReferralPressed,
  isTransparentBackground,
  hideBorder,
  shouldOverlapPageContent,
  shouldShowLanguageSelector,
  onLanguageSelectorGetHelpPress,
  beforeLaunch,
  showAICoachIcon,
  shouldShowSearch,
  onSearchPressed,
  isBlurBackground,
  logoFillColor,
}) => {
  const { formatMessage } = useIntl()
  const { colors, isDarkMode, breakpoints, isAuroraTheme } = useTheme() as ThemeType
  const { isSmartSearchEnabled } = useContext(AppContext)

  // in Aurora the Care Navigator entry point moves to the hamburger
  // menu on mobile; in the legacy theme it stays in the header at all sizes.
  const isAuroraAndMobileSized = breakpoints.isMobileSized && isAuroraTheme
  const shouldShowCareNavigator =
    shouldShowCareNavigatorHeaderEntrypoint && careNavigatorEntryPoint && !isAuroraAndMobileSized
  const shouldShowCareNavigatorOverLogo = shouldShowCareNavigator && breakpoints.isMobileSized
  const shouldShowCareNavigatorOnRight = shouldShowCareNavigator && !breakpoints.isMobileSized
  const activeNavTab = navItems?.filter((item) => {
    return item.condition && item.condition() && getIsActiveNavItem(item, currentPathname, currentScreen)
  })
  const shouldShowNavTabBar = activeNavTab ? activeNavTab.length > 0 : false
  const shouldShowSeparateLoginAndSignUp = breakpoints.isMinWidthMobileXs
  const shouldShowSmallerSizeLoginAndSignUp = breakpoints.isMobileSized
  /** One off button sizings that aren't finalized in design system yet. */
  const loginAndSignUpButtonTextSize =
    shouldShowSmallerSizeLoginAndSignUp && !isAuroraTheme ? BodyTextSize.CAPTION : undefined
  const loginAndSignUpButtonStyle = {
    ...(shouldShowSmallerSizeLoginAndSignUp && { height: 34 }),
    ...(isAuroraTheme && { height: 48 }),
  }
  const signUpButtonProps = {
    text: shouldShowSeparateLoginAndSignUp ? (
      <FormattedMessage
        defaultMessage='Sign up'
        description='In the page header, a button that directs the user to sign up'
      />
    ) : (
      <FormattedMessage defaultMessage='Log in/Sign Up' description='In the page header, a button to the login page' />
    ),
    onPress: () => {
      if (shouldShowSeparateLoginAndSignUp) {
        onSignUpPressed && onSignUpPressed()
      } else {
        onLoginPressed && onLoginPressed()
      }
    },
    testID: tID('SignUp-button'),
    customTextSize: loginAndSignUpButtonTextSize,
    style: loginAndSignUpButtonStyle,
  }
  const negativeTopInset = -1 * topInset
  const minNavBarHeight = 56 + topInset

  // Using ?? allows explicit `false` to disable blur, whereas || would default to isAuroraTheme
  const isBlur = isBlurBackground ?? isAuroraTheme
  const isBlurOrTransparentBackground = isTransparentBackground || (!IS_WEB && isBlur)

  const getToolbarBackgroundColor = () => {
    if (IS_WEB && isAuroraTheme && isDarkMode && isBlurBackground) return 'transparent'
    if (IS_WEB && isAuroraTheme && isDarkMode) return colors.backgroundNavHeader
    if (isBlurOrTransparentBackground) return 'transparent'
    return colors.backgroundPrimary
  }

  const getTabs = () =>
    tabs.map(({ name, tabId }) => {
      return (
        <ToolbarTab
          key={`Tab-${name}`}
          name={name}
          onPress={onTabPressed ? () => onTabPressed(tabId) : noop}
          active={activeTabId === tabId}
        />
      )
    })

  const pressableStyle = {
    hoveredBackgroundColor: colors.tertiaryButtonBackgroundHover,
    pressedBackgroundColor: colors.tertiaryButtonBackgroundPressed,
    styleByState: (pressed: boolean, focusVisible: boolean) => {
      const style: ViewStyle = {}
      if (pressed) {
        style.boxShadow = getFocusBoxShadow({ colors, outerBoxShadowSize: '6px' }).boxShadow
        style.backgroundColor = colors.tertiaryButtonBackgroundPressed
      }
      if (focusVisible && !pressed) {
        style.backgroundColor = colors.tertiaryButtonBackgroundHover
      }
      return style
    },
  }

  const showTitle = (showBack || showClose) && Boolean(title)
  const showTabs = !showBack && tabs.length > 0

  const shouldShowFavoritedProvidersStarIcon =
    favoritedProvidersEntrypointNavProps?.shouldShowFavoritedProvidersStarIcon || false
  const handleFavoritedProvidersStarIconPress =
    favoritedProvidersEntrypointNavProps?.onFavoritedProvidersStarIconPressed || noop
  const isFavoritedProvidersStarIconFilled =
    favoritedProvidersEntrypointNavProps?.isFavoritedProvidersStarIconFilled || false
  const onHoverForIconExplanation = favoritedProvidersEntrypointNavProps?.onHoverForIconExplanation || noop
  const favoritedProvidersDefaultIcon = () => {
    return (
      <FavoritesButtonContainer>
        <FavoritedProvidersStarIconButton
          isNavBarButton
          onPressStarIconButton={handleFavoritedProvidersStarIconPress}
          isFilled={isFavoritedProvidersStarIconFilled}
          onIconHover={onHoverForIconExplanation}
          buttonStyle={isAuroraTheme ? { padding: 12 } : undefined}
        />
      </FavoritesButtonContainer>
    )
  }
  const getFavoritedProvidersIcon = () => {
    return isAuroraTheme ? (
      <AuroraIconCircle>{favoritedProvidersDefaultIcon()}</AuroraIconCircle>
    ) : (
      favoritedProvidersDefaultIcon()
    )
  }
  const favoritedProvidersIcon = shouldShowFavoritedProvidersStarIcon ? getFavoritedProvidersIcon() : null

  const messagesIcon = (
    <HeaderMessagingIcon
      showGenAi={showAICoachIcon && unreadMessages === 0}
      showUnread={unreadMessages > 0}
      onPress={onMessagesPressed}
      hitSlop={10}
      testID={'MessageIcon'}
    />
  )

  const navMenu = (
    <>
      {headerNavMenuProps && (
        <MenuButtonContainer>
          <HeaderNavMenu
            {...headerNavMenuProps}
            highlightTrigger
            pressableTriggerStyle={isAuroraTheme ? undefined : pressableStyle.styleByState}
            shouldShowFavoritedProvidersStarIcon={() => shouldShowFavoritedProvidersStarIcon}
            handleFavoritedProvidersStarIconPress={handleFavoritedProvidersStarIconPress}
          />
        </MenuButtonContainer>
      )}
    </>
  )

  const backIcon = (
    <PressableOpacity
      onPress={onBackPressed}
      hitSlop={10}
      testID={tID('GoBack-button')}
      accessibilityLabel={formatMessage({
        defaultMessage: 'Go back',
        description: 'Button to go back',
      })}
    >
      <ArrowIcon fillColor={colors.iconDefault} direction={ArrowIconDirection.LEFT} />
    </PressableOpacity>
  )

  const closeIcon = (
    <PressableOpacity
      onPress={onClosePressed}
      hitSlop={10}
      testID={tID('LightHomeToolbar-close-button')}
      accessibilityLabel={formatMessage({
        defaultMessage: 'Close',
        description: 'Accessibility label for close button',
      })}
    >
      <CloseIcon fillColor={colors.iconDefault} size={16} />
    </PressableOpacity>
  )

  const careNavigatorButton = careNavigatorEntryPoint ? (
    <CareNavigatorButtonContainer
      accessible
      accessibilityLabel={formatMessage({
        defaultMessage: 'Care Navigator',
        description: 'Care navigator entry point',
      })}
      testID={tID('Care-Navigator-Global-EntryPoint-Button')}
      isOnLeft
    >
      {/* Always false: Aurora toolbar bg is cobalt_20 (light) / cobalt_70 (dark), not white.
          lightBgColor:true would render cobalt_20 circle on cobalt_20 bg = invisible. */}
      {React.cloneElement(careNavigatorEntryPoint, { lightBgColor: false })}
    </CareNavigatorButtonContainer>
  ) : null

  const messageButton = messagesPopover ? (
    <MessagesPopoverContainer>{messagesPopover}</MessagesPopoverContainer>
  ) : onMessagesPressed ? (
    messagesIcon
  ) : null

  const searchButton = (
    <SecondaryIconButtonStyled
      leftIcon={<SearchStarIcon />}
      onPress={() => {
        onSearchPressed?.()
      }}
      testID={tID('HeaderSearchButton')}
      accessibilityLabel={formatMessage({
        defaultMessage: 'Search',
        description: 'Button to search',
      })}
    />
  )

  const getMainNavContents = () => {
    return (
      <>
        <LeftContainer>
          {showBack ? (
            backIcon
          ) : shouldShowCareNavigatorOverLogo ? (
            careNavigatorButton
          ) : showLogo && !isAuroraAndMobileSized ? (
            <LogoButtonContainer
              onPress={onLogoPressed}
              accessibilityRole='button'
              accessibilityLabel={formatMessage({
                defaultMessage: 'Lyra Logo',
                description: 'Button that takes user to home page',
              })}
              testID={tID('Header-Lyra-Logo')}
              {...pressableStyle}
            >
              <LyraLogo
                width={54}
                height={31}
                fillColor={
                  logoFillColor ??
                  (isAuroraTheme
                    ? isDarkMode
                      ? colors.textInverse
                      : colors.iconDefault
                    : colors.icons.lyraLogoPrimaryIcon.fill)
                }
              />
            </LogoButtonContainer>
          ) : null}
          {!showBack &&
            shouldShowCareNavigatorOverLogo &&
            shouldShowFavoritedProvidersStarIcon &&
            favoritedProvidersIcon}
        </LeftContainer>
        {navItems && breakpoints.isMinWidthLaptop && !showBack && !showClose && (
          <NavItemsContainer role='list'>
            {navItems.map((item) => {
              const isActive = getIsActiveNavItem(item, currentPathname, currentScreen)
              return item.condition && item.condition() ? (
                <NavItemButton
                  key={item.id}
                  testID={tID(`Header-${item.id}`)}
                  onPress={item.onPressItem}
                  role='listitem'
                  {...pressableStyle}
                >
                  <Title
                    text={item.text}
                    size={TitleSize.XSMALL}
                    regular
                    color={isActive ? colors.textActive : colors.textPrimary}
                    noRole
                  />
                  {isActive && <NavItemButtonActiveUnderline testID={tID(`Header-${item.id}-active`)} />}
                </NavItemButton>
              ) : (
                <></>
              )
            })}
          </NavItemsContainer>
        )}
        {showTitle && (
          <PageTitleContainer
            style={isBlur && !IS_WEB ? { top: topInset, bottom: 0, justifyContent: 'center' } : undefined}
          >
            <Title text={title} size={textSize} textAlign='center' />
          </PageTitleContainer>
        )}
        <RightContainer>
          {shouldShowReferral && onReferralPressed && <ReferButton onPress={onReferralPressed} />}
          {shouldShowLanguageSelector && (
            <LanguageSelector
              onGetHelpPress={onLanguageSelectorGetHelpPress}
              beforeLaunch={beforeLaunch}
              {...(logoFillColor && { customTextColor: logoFillColor })}
            />
          )}
          {shouldShowFavoritedProvidersStarIcon && breakpoints.isMinWidthTablet && favoritedProvidersIcon}

          {shouldShowCareNavigatorOnRight && careNavigatorButton}
          {shouldShowLoginAndSignUpButtons && (
            <LoginAndSignUpContainer>
              {shouldShowSeparateLoginAndSignUp && (
                <LoginButton
                  text={
                    <FormattedMessage
                      defaultMessage='Log in'
                      description='In the page header, a button to the login page'
                    />
                  }
                  onPress={() => {
                    onLoginPressed && onLoginPressed()
                  }}
                  testID={tID('SignIn-button')}
                  customTextSize={loginAndSignUpButtonTextSize}
                  style={loginAndSignUpButtonStyle}
                  isSmall
                />
              )}
              {isAuroraTheme ? (
                <SecondarySignUpButton {...signUpButtonProps} />
              ) : (
                <SignUpButton {...signUpButtonProps} size={ButtonSize.SMALL} />
              )}
            </LoginAndSignUpContainer>
          )}
          {showMessages && messageButton}
          {shouldShowSearch && isSmartSearchEnabled && searchButton}
          {showNavMenu && navMenu}
          {showClose && closeIcon}
          {showExit && onClosePressed && (
            <StyledXButton testID={tID(`LightHomeToolbar-exitButton`)} onPress={onClosePressed} />
          )}
        </RightContainer>
      </>
    )
  }

  return (
    <>
      <Toolbar
        topInset={isBlur && !IS_WEB ? 0 : topInset}
        backgroundColor={getToolbarBackgroundColor()}
        containerStyle={{
          width: '100%',
          ...(shouldOverlapPageContent && { height: 0 }),
          ...(isBlur && {
            position: 'sticky',
            top: 0,
            right: 0,
            left: 0,
            height: IS_WEB ? NAV_HEADER_HEIGHT : minNavBarHeight,
          }),
          ...(isBlurBackground && IS_WEB && { backdropFilter: 'blur(30px)' }),
        }}
      >
        <Container
          testID={testID || tID('LightHomeToolbar')}
          isTransparentBackground={isBlurOrTransparentBackground}
          hideBorder={hideBorder}
          hidePadding={isBlur}
          isWebBlur={IS_WEB && isAuroraTheme && isDarkMode && !!isBlurBackground}
        >
          <HeaderRow isBlur={isBlur}>
            {isBlur && !IS_WEB ? (
              <BlurViewStyled
                intensity={70}
                tint={isDarkMode ? 'dark' : 'light'}
                negativeTopInset={negativeTopInset}
                minNavBarHeight={minNavBarHeight}
                isTransparentBackground={isTransparentBackground}
              >
                <GradientHeader />
                {getMainNavContents()}
              </BlurViewStyled>
            ) : (
              <>
                {IS_WEB && isAuroraTheme && isDarkMode && isBlurBackground && (
                  <WebScrollGradient
                    colors={[colors.gradientWebHeaderStart, colors.gradientWebHeaderEnd]}
                    start={[0, 0]}
                    end={[0, 1]}
                  />
                )}
                {isBlur &&
                  IS_WEB &&
                  !isDarkMode &&
                  !isBlurBackground &&
                  !(isAuroraTheme && isTransparentBackground) && <GradientHeader />}
                {getMainNavContents()}
              </>
            )}
          </HeaderRow>
          {showTabs && (
            <ScrollView horizontal bounces={false}>
              {getTabs()}
            </ScrollView>
          )}
        </Container>
      </Toolbar>
      {careNavigatorModal}
      {navItems &&
        !breakpoints.isMinWidthLaptop &&
        IS_WEB &&
        shouldShowNavTabBar &&
        (isAuroraTheme ? (
          <FloatingTabBar
            testID={tID(`${testID}FloatingTabBar`)}
            tabs={navItems
              .filter((item) => item.condition?.() ?? true)
              .map((item) => {
                const result = {
                  key: item.id,
                  label: item.text,
                  icon: (isFocused: boolean, iconColor: string) =>
                    item.floatingIcon?.(iconColor) ?? item.icon?.(isFocused) ?? null,
                  onPress: item.onPressItem ?? noop,
                  testID: tID(`${testID}FloatingTabBar-tab-${item.id}`),
                } as FloatingTabBarTab
                return result
              })}
            activeTabKey={activeNavTab?.[0]?.id ?? ''}
          />
        ) : (
          <TabBar
            testID={tID(`${testID}TabBar`)}
            tabs={navItems}
            currentPathname={currentPathname}
            currentScreen={currentScreen}
            isFixedToBottom
          />
        ))}
    </>
  )
}
